import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdChevronRight,
  MdEdit,
  MdExpandMore,
  MdApartment,
  MdLocationOn,
} from 'react-icons/md';
import {
  AppCard,
  AppInput,
  GooglePlacesSearch,
  ManualAddressWithMap,
  OutlineButton,
  PrimaryButton,
  SortableTh,
  StatusBadge,
  PageLoader,
} from '../../../../components/ui';
import { useTableSort } from '../../../../hooks/useTableSort';
import { ActiveBadge } from '../MasterDataPage';
import { findVillageById } from '../../../../data/master/villages';
import { WORK_AREA_OPTIONS } from '../../../../data/master/indents';
import {
  createBusinessUnit,
  createSite,
  listBusinessUnits,
  listSites,
  updateBusinessUnit,
  updateSite,
} from '../../../../repositories/businessUnit.repository';
import { lookupVillages } from '../../../../repositories/lookup.repository';

const PROJECT_OPERATION_OPTIONS = WORK_AREA_OPTIONS; // ['Project', 'Operation']

const emptyBuForm = {
  shortName: '',
  fullNameGst: '',
  address1: '',
  address2: '',
  googlePlaceId: '',
  formattedAddress: '',
  locality: '',
  sublocality: '',
  taluka: '',
  district: '',
  state: '',
  country: '',
  pin: '',
  latitude: null,
  longitude: null,
  villageId: '',
  villageDbId: null,
  active: true,
};

const emptySiteForm = {
  buId: '',
  buDbId: null,
  siteName: '',
  projectOperation: 'Project',
  address1: '',
  address2: '',
  googlePlaceId: '',
  formattedAddress: '',
  locality: '',
  sublocality: '',
  taluka: '',
  district: '',
  state: '',
  country: '',
  pin: '',
  latitude: null,
  longitude: null,
  villageId: '',
  villageDbId: null,
  pinOverride: '',
  active: true,
};

function googleMapsUrl({ latitude, longitude, googlePlaceId, formattedAddress, locality }) {
  const lat = latitude != null ? Number(latitude) : NaN;
  const lng = longitude != null ? Number(longitude) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lng}`)}`;
  }
  if (googlePlaceId) {
    return `https://www.google.com/maps/search/?api=1&query=place_id:${encodeURIComponent(googlePlaceId)}`;
  }
  const q = String(formattedAddress || locality || '').trim();
  if (q) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  return null;
}

/** Keyless embed URL for in-app map preview (iframe). */
function googleMapsEmbedUrl({ latitude, longitude, formattedAddress, locality }) {
  const lat = latitude != null ? Number(latitude) : NaN;
  const lng = longitude != null ? Number(longitude) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(`${lat},${lng}`)}&z=15&output=embed`;
  }
  const q = String(formattedAddress || locality || '').trim();
  if (q) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=14&output=embed`;
  }
  return null;
}

function MapPreviewModal({ isOpen, onClose, location }) {
  const title = location?.title || 'Location';
  const subtitle = location?.subtitle || '';
  const embedUrl = location
    ? googleMapsEmbedUrl(location)
    : null;
  const externalUrl = location ? googleMapsUrl(location) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent
        maxW={{ base: '96vw', md: '1100px' }}
        mx={4}
        my={4}
        maxH="90vh"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <ModalHeader flexShrink={0} pb={2}>
          <Text fontSize="lg" fontWeight="700">
            {title}
          </Text>
          {subtitle ? (
            <Text fontSize="13px" color="text.secondary" fontWeight="500" mt={1}>
              {subtitle}
            </Text>
          ) : null}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody flex="1" overflowY="auto" minH={0} pt={0}>
          {embedUrl ? (
            <Box
              borderRadius="md"
              overflow="hidden"
              borderWidth="1px"
              borderColor="border.subtle"
              h={{ base: '420px', md: '620px' }}
              bg="bg.input"
            >
              <Box
                as="iframe"
                title={`Map — ${title}`}
                src={embedUrl}
                w="100%"
                h="100%"
                border="0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </Box>
          ) : (
            <Text color="text.secondary" fontSize="14px">
              Map preview is not available for this location.
            </Text>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          <OutlineButton type="button" onClick={onClose}>
            Close
          </OutlineButton>
          {externalUrl ? (
            <PrimaryButton
              as="a"
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </PrimaryButton>
          ) : null}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function VillageCell({
  villageId,
  villageDbId,
  villageName,
  locality,
  pin,
  villages,
  latitude,
  longitude,
  googlePlaceId,
  formattedAddress,
  onViewMap,
}) {
  const v =
    findVillageById(villages, villageId) ||
    findVillageById(villages, villageDbId);
  const canShowMap = Boolean(
    googleMapsEmbedUrl({
      latitude,
      longitude,
      formattedAddress,
      locality: villageName || locality,
    }) ||
      googleMapsUrl({
        latitude,
        longitude,
        googlePlaceId,
        formattedAddress,
        locality: villageName || locality,
      }),
  );
  const mapLink = canShowMap ? (
    <Link
      as="button"
      type="button"
      onClick={() =>
        onViewMap?.({
          title: villageName || locality || v?.villageName || 'Location',
          subtitle: [
            pin ? `PIN ${pin}` : null,
            formattedAddress,
          ]
            .filter(Boolean)
            .join(' · '),
          latitude,
          longitude,
          googlePlaceId,
          formattedAddress,
          locality: villageName || locality,
        })
      }
      color="utkarsh.amber"
      fontWeight="600"
      textDecoration="underline"
      _hover={{ opacity: 0.85, textDecoration: 'underline' }}
    >
      View on map
    </Link>
  ) : null;

  if (!v) {
    const label = villageName || locality;
    if (label) {
      return (
        <Box>
          <Text fontSize="13px" fontWeight="600">
            {label}
          </Text>
          <Text fontSize="12px" color="text.secondary">
            {[pin ? `PIN ${pin}` : null, mapLink || 'Google location']
              .filter(Boolean)
              .reduce((acc, part, i) => {
                if (i === 0) return [part];
                return [...acc, ' · ', part];
              }, [])}
          </Text>
        </Box>
      );
    }
    return (
      <Text fontSize="13px" color="text.secondary">
        —
      </Text>
    );
  }
  return (
    <Box>
      <Text fontSize="13px" fontWeight="600">
        {v.villageName}
      </Text>
      <Text fontSize="12px" color="text.secondary">
        {[
          v.villageId,
          v.talukaName,
          v.pin,
          mapLink,
        ]
          .filter(Boolean)
          .reduce((acc, part, i) => {
            if (i === 0) return [part];
            return [...acc, ' · ', part];
          }, [])}
      </Text>
    </Box>
  );
}

function ReadonlyField({ label, value }) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Flex
        align="center"
        px={4}
        h="52px"
        bg="bg.input"
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
      >
        <Text fontSize="15px" fontWeight="600" noOfLines={1}>
          {value || '—'}
        </Text>
      </Flex>
    </FormControl>
  );
}

function ActiveRow({ checked, onChange, label = 'Active' }) {
  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <Flex
        align="center"
        justify="space-between"
        px={4}
        h="52px"
        bg="bg.input"
        borderRadius="sm"
        borderWidth="1px"
        borderColor="border.subtle"
      >
        <Text fontSize="15px" fontWeight="500" color="text.secondary">
          {checked ? 'Enabled' : 'Disabled'}
        </Text>
        <Switch isChecked={checked} onChange={onChange} colorScheme="yellow" />
      </Flex>
    </FormControl>
  );
}

export default function BusinessUnitsPage({ embedded = false } = {}) {
  const toast = useToast();
  const [villages, setVillages] = useState([]);
  const [bus, setBus] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const {
    sorted: sortedBus,
    sort,
    toggleSort,
  } = useTableSort(bus, {
    buName: (b) => b.buName || b.shortName,
  });
  const [expanded, setExpanded] = useState(() => new Set());
  const [mode, setMode] = useState('bu'); // bu | site
  const [editingId, setEditingId] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [buForm, setBuForm] = useState(emptyBuForm);
  const [siteForm, setSiteForm] = useState(emptySiteForm);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isMapOpen,
    onOpen: onMapOpen,
    onClose: onMapClose,
  } = useDisclosure();
  const [mapLocation, setMapLocation] = useState(null);

  const openMapPreview = (location) => {
    setMapLocation(location);
    onMapOpen();
  };

  const loadAll = useCallback(
    async ({ showSpinner = true } = {}) => {
      if (showSpinner) setLoading(true);
      try {
        const [buResult, siteResult, villageOptions] = await Promise.all([
          listBusinessUnits({ pageSize: 500 }),
          listSites({ pageSize: 500 }),
          lookupVillages(),
        ]);
        const nextBus = buResult.items || [];
        const nextSites = siteResult.items || [];
        setBus(nextBus);
        setSites(nextSites);
        setVillages(villageOptions || []);
        setExpanded((prev) => {
          if (prev.size > 0) return prev;
          return new Set(nextBus.filter((b) => b.id != null).map((b) => b.id));
        });
      } catch (err) {
        toast({
          title: err?.message || 'Failed to load business units',
          status: 'error',
          position: 'top-right',
        });
        setBus([]);
        setSites([]);
        setVillages([]);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [toast],
  );

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const sitesByBu = useMemo(() => {
    const map = {};
    sites.forEach((s) => {
      const keys = [s.buDbId, s.buId]
        .filter((k) => k != null && k !== '')
        .map((k) => (typeof k === 'number' ? k : Number(k) || k));
      keys.forEach((key) => {
        if (!map[key]) map[key] = [];
        if (!map[key].includes(s)) map[key].push(s);
      });
    });
    return map;
  }, [sites]);

  const resolveVillageCode = (row) => {
    if (row?.villageId) return row.villageId;
    const village = findVillageById(villages, row?.villageDbId);
    return village?.villageId || '';
  };

  const resolveVillageDbId = (villageCodeOrId) => {
    const village = findVillageById(villages, villageCodeOrId);
    return village?.id ?? null;
  };

  const openAddBu = () => {
    setMode('bu');
    setEditingId(null);
    setEditingCode(null);
    setBuForm(emptyBuForm);
    onOpen();
  };

  const openEditBu = (bu) => {
    setMode('bu');
    setEditingId(bu.id);
    setEditingCode(bu.buId || null);
    setBuForm({
      ...emptyBuForm,
      shortName: bu.shortName || '',
      fullNameGst: bu.fullNameGst || '',
      address1: bu.address1 || '',
      address2: bu.address2 || '',
      googlePlaceId: bu.googlePlaceId || '',
      formattedAddress: bu.formattedAddress || '',
      locality: bu.locality || '',
      sublocality: bu.sublocality || '',
      taluka: bu.taluka || '',
      district: bu.district || '',
      state: bu.state || '',
      country: bu.country || '',
      pin: bu.pin || '',
      latitude: bu.latitude ?? null,
      longitude: bu.longitude ?? null,
      villageId: resolveVillageCode(bu),
      villageDbId: bu.villageDbId ?? null,
      active: bu.active !== false,
    });
    onOpen();
  };

  const openAddSite = (bu) => {
    setMode('site');
    setEditingId(null);
    setEditingCode(null);
    setSiteForm({
      ...emptySiteForm,
      buId: bu.buId || (bu.id != null ? String(bu.id) : ''),
      buDbId: bu.id ?? null,
      villageId: resolveVillageCode(bu),
    });
    onOpen();
  };

  const openEditSite = (site) => {
    setMode('site');
    setEditingId(site.id);
    setEditingCode(site.siteId || null);
    setSiteForm({
      ...emptySiteForm,
      buId: site.buId || (site.buDbId != null ? String(site.buDbId) : ''),
      buDbId: site.buDbId ?? null,
      siteName: site.siteName || '',
      projectOperation: PROJECT_OPERATION_OPTIONS.includes(site.projectOperation)
        ? site.projectOperation
        : 'Project',
      address1: site.address1 || '',
      address2: site.address2 || '',
      googlePlaceId: site.googlePlaceId || '',
      formattedAddress: site.formattedAddress || '',
      locality: site.locality || '',
      sublocality: site.sublocality || '',
      taluka: site.taluka || '',
      district: site.district || '',
      state: site.state || '',
      country: site.country || '',
      pin: site.pin || '',
      latitude: site.latitude ?? null,
      longitude: site.longitude ?? null,
      villageId: resolveVillageCode(site),
      villageDbId: site.villageDbId ?? null,
      pinOverride: site.pinOverride || '',
      active: site.active !== false,
    });
    onOpen();
  };

  const toggle = (buId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(buId)) next.delete(buId);
      else next.add(buId);
      return next;
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (mode === 'bu') {
      if (!buForm.shortName.trim()) {
        toast({
          title: 'Short name is required',
          status: 'warning',
          position: 'top-right',
        });
        return;
      }
      if (
        !buForm.googlePlaceId &&
        !buForm.formattedAddress &&
        !buForm.locality &&
        !buForm.address1 &&
        !buForm.villageDbId &&
        !buForm.villageId
      ) {
        toast({
          title: 'Select a Google location (or address)',
          status: 'warning',
          position: 'top-right',
        });
        return;
      }
      const villageDbId =
        buForm.villageDbId != null
          ? buForm.villageDbId
          : resolveVillageDbId(buForm.villageId);
      const payload = {
        shortName: buForm.shortName.trim(),
        fullNameGst: buForm.fullNameGst.trim(),
        address1: buForm.address1.trim(),
        address2: buForm.address2.trim(),
        googlePlaceId: buForm.googlePlaceId || null,
        formattedAddress: buForm.formattedAddress || null,
        locality: buForm.locality || null,
        sublocality: buForm.sublocality || null,
        taluka: buForm.taluka || null,
        district: buForm.district || null,
        state: buForm.state || null,
        country: buForm.country || null,
        pin: buForm.pin || null,
        latitude: buForm.latitude,
        longitude: buForm.longitude,
        villageId: villageDbId,
        villageDbId,
        active: buForm.active,
      };
      setSaving(true);
      try {
        if (editingId != null) {
          await updateBusinessUnit(editingId, payload);
          toast({
            title: 'Business Unit updated',
            description: `${payload.shortName}${editingCode ? ` · ${editingCode}` : ''}`,
            status: 'success',
            position: 'top-right',
          });
        } else {
          const created = await createBusinessUnit(payload);
          toast({
            title: 'Business Unit saved',
            description: `${payload.shortName}${created?.buId ? ` · ${created.buId}` : ''}`,
            status: 'success',
            position: 'top-right',
          });
        }
        onClose();
        await loadAll({ showSpinner: false });
      } catch (err) {
        toast({
          title: err?.message || 'Failed to save business unit',
          status: 'error',
          position: 'top-right',
        });
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!siteForm.siteName.trim() || (siteForm.buDbId == null && !siteForm.buId)) {
      toast({
        title: 'Site name and BU are required',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    if (
      !siteForm.googlePlaceId &&
      !siteForm.formattedAddress &&
      !siteForm.locality &&
      !siteForm.address1 &&
      !siteForm.villageDbId &&
      !siteForm.villageId
    ) {
      toast({
        title: 'Select a Google location (or address)',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const parentBu =
      bus.find((b) => b.id === siteForm.buDbId) ||
      bus.find((b) => b.buId === siteForm.buId);
    const buDbId = siteForm.buDbId ?? parentBu?.id ?? null;
    const villageDbId =
      siteForm.villageDbId != null
        ? siteForm.villageDbId
        : resolveVillageDbId(siteForm.villageId);
    if (buDbId == null) {
      toast({
        title: 'Selected business unit is invalid',
        status: 'warning',
        position: 'top-right',
      });
      return;
    }
    const payload = {
      buId: siteForm.buId || String(buDbId),
      buDbId,
      siteName: siteForm.siteName.trim(),
      projectOperation: siteForm.projectOperation.trim(),
      address1: siteForm.address1.trim(),
      address2: siteForm.address2.trim(),
      googlePlaceId: siteForm.googlePlaceId || null,
      formattedAddress: siteForm.formattedAddress || null,
      locality: siteForm.locality || null,
      sublocality: siteForm.sublocality || null,
      taluka: siteForm.taluka || null,
      district: siteForm.district || null,
      state: siteForm.state || null,
      country: siteForm.country || null,
      pin: siteForm.pin || null,
      latitude: siteForm.latitude,
      longitude: siteForm.longitude,
      villageId: villageDbId,
      villageDbId,
      pinOverride: siteForm.pinOverride.trim() || siteForm.pin || '',
      active: siteForm.active,
    };
    setSaving(true);
    try {
      if (editingId != null) {
        await updateSite(editingId, payload);
        toast({
          title: 'Site updated',
          description: `${payload.siteName}${editingCode ? ` · ${editingCode}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      } else {
        const created = await createSite(payload);
        setExpanded((prev) => new Set([...prev, buDbId]));
        toast({
          title: 'Site saved',
          description: `${payload.siteName}${created?.siteId ? ` · ${created.siteId}` : ''}`,
          status: 'success',
          position: 'top-right',
        });
      }
      onClose();
      await loadAll({ showSpinner: false });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to save site',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader label="Loading business units…" />;
  }

  return (
    <Box>
      <Flex
        justify="space-between"
        align={{ base: 'start', md: 'center' }}
        gap={4}
        mb={6}
        wrap="wrap"
      >
        {!embedded ? (
          <Box>
            <Heading size="lg">Business Unit & Site</Heading>
            <Text color="text.secondary" mt={2} maxW="720px">
              Two layers only: <Text as="span" fontWeight="600">Business Unit</Text> →{' '}
              <Text as="span" fontWeight="600">Site</Text>. Location comes from Google Places;
              Village Master is optional and auto-matched when possible.
            </Text>
          </Box>
        ) : (
          <Box />
        )}
        <PrimaryButton size="sm" leftIcon={<MdAdd />} onClick={openAddBu}>
          Add Business Unit
        </PrimaryButton>
      </Flex>

      <AppCard p={0} overflow="hidden">
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="bg.input">
              <Tr>
                <SortableTh columnKey="buName" sort={sort} onSort={toggleSort}>
                  Name
                </SortableTh>
                <SortableTh columnKey="buId" sort={sort} onSort={toggleSort}>
                  ID
                </SortableTh>
                <Th>Type</Th>
                <SortableTh columnKey="villageId" sort={sort} onSort={toggleSort}>
                  Location
                </SortableTh>
                <SortableTh columnKey="active" sort={sort} onSort={toggleSort}>
                  Status
                </SortableTh>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {sortedBus.map((bu) => {
                const children = sitesByBu[bu.id] || sitesByBu[bu.buId] || [];
                const isRowOpen = expanded.has(bu.id);
                return (
                  <React.Fragment key={bu.id ?? bu.buId}>
                    <Tr _hover={{ bg: 'bg.input' }}>
                      <Td>
                        <Flex align="center" gap={1}>
                          {children.length ? (
                            <IconButton
                              aria-label={isRowOpen ? 'Collapse' : 'Expand'}
                              icon={
                                <Icon as={isRowOpen ? MdExpandMore : MdChevronRight} boxSize={5} />
                              }
                              size="xs"
                              variant="ghostInk"
                              onClick={() => toggle(bu.id)}
                            />
                          ) : (
                            <Box w="28px" />
                          )}
                          <Icon as={MdApartment} color="utkarsh.amber" boxSize={4} mr={1} />
                          <Box>
                            <Text fontWeight="700">{bu.shortName}</Text>
                            <Text fontSize="12px" color="text.secondary" noOfLines={1}>
                              {bu.fullNameGst || '—'}
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      <Td fontFamily="mono" fontSize="13px">
                        {bu.id ?? bu.buId}
                      </Td>
                      <Td>
                        <StatusBadge status="info">Business Unit</StatusBadge>
                      </Td>
                      <Td>
                        <VillageCell
                          villageId={bu.villageId}
                          villageDbId={bu.villageDbId}
                          villageName={bu.villageName}
                          locality={bu.locality}
                          pin={bu.pin}
                          villages={villages}
                          latitude={bu.latitude}
                          longitude={bu.longitude}
                          googlePlaceId={bu.googlePlaceId}
                          formattedAddress={bu.formattedAddress}
                          onViewMap={openMapPreview}
                        />
                      </Td>
                      <Td>
                        <ActiveBadge active={bu.active} />
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <IconButton
                            aria-label={`Edit ${bu.shortName}`}
                            icon={<MdEdit />}
                            size="sm"
                            variant="ghostInk"
                            onClick={() => openEditBu(bu)}
                          />
                          <OutlineButton
                            size="sm"
                            leftIcon={<MdAdd />}
                            onClick={() => openAddSite(bu)}
                          >
                            Add site
                          </OutlineButton>
                        </HStack>
                      </Td>
                    </Tr>
                    {isRowOpen
                      ? children.map((site) => (
                          <Tr key={site.id ?? site.siteId} _hover={{ bg: 'bg.input' }}>
                            <Td>
                              <Flex align="center" pl="48px" gap={2}>
                                <Icon as={MdLocationOn} color="text.secondary" boxSize={4} />
                                <Box>
                                  <Text fontWeight="600">{site.siteName}</Text>
                                  <Text fontSize="12px" color="text.secondary">
                                    {site.projectOperation || '—'}
                                  </Text>
                                </Box>
                              </Flex>
                            </Td>
                            <Td fontFamily="mono" fontSize="13px">
                              {site.siteId}
                            </Td>
                            <Td>
                              <StatusBadge status="verified">Site</StatusBadge>
                            </Td>
                            <Td>
                              <VillageCell
                                villageId={site.villageId}
                                villageDbId={site.villageDbId}
                                villageName={site.villageName}
                                locality={site.locality}
                                pin={site.pin}
                                villages={villages}
                                latitude={site.latitude}
                                longitude={site.longitude}
                                googlePlaceId={site.googlePlaceId}
                                formattedAddress={site.formattedAddress}
                                onViewMap={openMapPreview}
                              />
                            </Td>
                            <Td>
                              <ActiveBadge active={site.active} />
                            </Td>
                            <Td>
                              <IconButton
                                aria-label={`Edit ${site.siteName}`}
                                icon={<MdEdit />}
                                size="sm"
                                variant="ghostInk"
                                onClick={() => openEditSite(site)}
                              />
                            </Td>
                          </Tr>
                        ))
                      : null}
                  </React.Fragment>
                );
              })}
              {sortedBus.length === 0 ? (
                <Tr>
                  <Td colSpan={6}>
                    <Text color="text.secondary" py={6} textAlign="center">
                      No business units yet.
                    </Text>
                  </Td>
                </Tr>
              ) : null}
            </Tbody>
          </Table>
        </Box>
      </AppCard>

      <Modal
        isOpen={isOpen}
        onClose={() => {
          if (!saving) onClose();
        }}
        size="xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.500" />
        <ModalContent
          as="form"
          onSubmit={handleSave}
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader flexShrink={0}>
            {mode === 'bu'
              ? editingId != null
                ? `Edit Business Unit · ${editingCode || editingId}`
                : 'Add Business Unit'
              : editingId != null
                ? `Edit Site · ${editingCode || editingId}`
                : 'Add Site'}
          </ModalHeader>
          <ModalCloseButton isDisabled={saving} />
          <ModalBody flex="1" overflowY="auto" minH={0} pb={4}>
              {mode === 'bu' ? (
                <VStack align="stretch" spacing={5}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} templateColumns={{ md: '1fr 1fr' }}>
                    <AppInput
                      label="Short Name"
                      isRequired
                      placeholder="e.g. APMUL"
                      value={buForm.shortName}
                      onChange={(e) => setBuForm((f) => ({ ...f, shortName: e.target.value }))}
                    />
                    <AppInput
                      label="Full Name as per GST"
                      placeholder="e.g. Adani Power Mundra Limited"
                      value={buForm.fullNameGst}
                      onChange={(e) => setBuForm((f) => ({ ...f, fullNameGst: e.target.value }))}
                    />
                  </SimpleGrid>

                  <GooglePlacesSearch
                    villages={villages}
                    helperText="Optional — select a place to prefill. You can always edit fields or pick on the map below."
                    onResolved={(place) => {
                      setBuForm((f) => ({
                        ...f,
                        googlePlaceId: place.googlePlaceId || '',
                        formattedAddress: place.formattedAddress || '',
                        address1:
                          place.address1 || place.formattedAddress || f.address1,
                        address2: place.address2 || f.address2,
                        locality: place.locality || place.village || '',
                        sublocality: place.sublocality || '',
                        taluka: place.taluka || '',
                        district: place.district || '',
                        state: place.state || '',
                        country: place.country || 'India',
                        pin: place.pin || '',
                        latitude: place.latitude ?? null,
                        longitude: place.longitude ?? null,
                        villageId: place.villageId || '',
                        villageDbId: place.matchedVillage?.id ?? null,
                      }));
                      toast({
                        title: 'Google location saved',
                        description: place.matchedVillage
                          ? `Will link Village Master · ${place.matchedVillage.villageName}`
                          : 'Village Master can be joined later when villages are added.',
                        status: 'success',
                        position: 'top-right',
                        duration: 2500,
                      });
                    }}
                  />

                  <ManualAddressWithMap
                    mapOpen={isOpen && mode === 'bu'}
                    form={buForm}
                    onChange={(patch) => setBuForm((f) => ({ ...f, ...patch }))}
                  />

                  <ActiveRow
                    checked={buForm.active}
                    onChange={(e) => setBuForm((f) => ({ ...f, active: e.target.checked }))}
                  />
                </VStack>
              ) : (
                <VStack align="stretch" spacing={5}>
                  <ReadonlyField
                    label="Business Unit"
                    value={
                      bus.find((b) => b.id === siteForm.buDbId)?.shortName ||
                      bus.find((b) => b.buId === siteForm.buId)?.shortName ||
                      siteForm.buId
                    }
                  />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} templateColumns={{ md: '1fr 1fr' }}>
                    <AppInput
                      label="Site Name"
                      isRequired
                      placeholder="e.g. Pocket 1"
                      value={siteForm.siteName}
                      onChange={(e) => setSiteForm((f) => ({ ...f, siteName: e.target.value }))}
                    />
                    <FormControl>
                      <FormLabel>Project or Operation</FormLabel>
                      <SimpleGrid columns={2} spacing={2}>
                        {PROJECT_OPERATION_OPTIONS.map((option) => {
                          const selected =
                            (siteForm.projectOperation || 'Project') === option;
                          return (
                            <Flex
                              key={option}
                              as="button"
                              type="button"
                              onClick={() =>
                                setSiteForm((f) => ({
                                  ...f,
                                  projectOperation: option,
                                }))
                              }
                              align="center"
                              justify="center"
                              h="52px"
                              borderRadius="sm"
                              borderWidth="1px"
                              borderColor={selected ? 'utkarsh.amber' : 'border.subtle'}
                              bg={selected ? 'utkarsh.amber' : 'bg.input'}
                              fontSize="15px"
                              fontWeight={selected ? '600' : '500'}
                              color={selected ? 'text.onAmber' : 'text.primary'}
                              transition="all 0.15s"
                              _hover={{
                                borderColor: selected ? 'utkarsh.amber' : 'text.secondary',
                              }}
                            >
                              {option}
                            </Flex>
                          );
                        })}
                      </SimpleGrid>
                    </FormControl>
                  </SimpleGrid>

                  <GooglePlacesSearch
                    villages={villages}
                    helperText="Optional — select a place to prefill. You can always edit fields or pick on the map below."
                    onResolved={(place) => {
                      setSiteForm((f) => ({
                        ...f,
                        googlePlaceId: place.googlePlaceId || '',
                        formattedAddress: place.formattedAddress || '',
                        address1:
                          place.address1 || place.formattedAddress || f.address1,
                        address2: place.address2 || f.address2,
                        locality: place.locality || place.village || '',
                        sublocality: place.sublocality || '',
                        taluka: place.taluka || '',
                        district: place.district || '',
                        state: place.state || '',
                        country: place.country || 'India',
                        pin: place.pin || '',
                        latitude: place.latitude ?? null,
                        longitude: place.longitude ?? null,
                        villageId: place.villageId || '',
                        villageDbId: place.matchedVillage?.id ?? null,
                        pinOverride: place.pin || f.pinOverride,
                      }));
                      toast({
                        title: 'Google location saved',
                        description: place.matchedVillage
                          ? `Will link Village Master · ${place.matchedVillage.villageName}`
                          : 'Village Master can be joined later when villages are added.',
                        status: 'success',
                        position: 'top-right',
                        duration: 2500,
                      });
                    }}
                  />

                  <ManualAddressWithMap
                    mapOpen={isOpen && mode === 'site'}
                    form={siteForm}
                    onChange={(patch) => setSiteForm((f) => ({ ...f, ...patch }))}
                    showPinOverride
                    pinOverride={siteForm.pinOverride}
                    onPinOverrideChange={(v) =>
                      setSiteForm((f) => ({ ...f, pinOverride: v }))
                    }
                  />

                  <ActiveRow
                    checked={siteForm.active}
                    onChange={(e) =>
                      setSiteForm((f) => ({ ...f, active: e.target.checked }))
                    }
                  />
                </VStack>
              )}
          </ModalBody>
          <ModalFooter flexShrink={0} gap={2} borderTopWidth="1px" borderColor="border.subtle">
            <OutlineButton type="button" onClick={onClose} isDisabled={saving}>
              Cancel
            </OutlineButton>
            <PrimaryButton type="submit" isLoading={saving}>
              Save
            </PrimaryButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <MapPreviewModal
        isOpen={isMapOpen}
        onClose={() => {
          onMapClose();
          setMapLocation(null);
        }}
        location={mapLocation}
      />
    </Box>
  );
}
